import { useState } from 'react'
import {
  BookOpen,
  Server,
  Code,
  Database,
  Layout,
  Rocket,
  Zap,
  FolderTree,
  ChevronDown,
  ChevronRight,
  FileText,
  Compass,
  Bot,
  Layers,
  Globe,
  Shield,
  Settings,
  Terminal,
  MessageSquare,
  Network,
  GitBranch,
  LayoutGrid,
  PlayCircle,
  CheckCircle,
  ArrowRight,
  Lightbulb,
  HelpCircle,
  Palette,
  FileEdit,
} from 'lucide-react'
import { RoutingSection } from './RoutingSection'
import { DesignSystemSection } from './DesignSystemSection'

type Section = 'overview' | 'user-guide' | 'architecture' | 'tech-stack' | 'structure' | 'database' | 'api' | 'frontend' | 'deployment' | 'getting-started' | 'routing' | 'design-system'

export function HelpPage() {
  const [activeSection, setActiveSection] = useState<Section>('overview')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['nav']))

  const toggleExpanded = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const navItems = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'user-guide', label: 'User Guide', icon: HelpCircle },
    { id: 'design-system', label: 'Design System', icon: Palette },
    { id: 'architecture', label: 'Architecture', icon: Server },
    { id: 'tech-stack', label: 'Tech Stack', icon: Code },
    { id: 'structure', label: 'Structure', icon: FolderTree },
    { id: 'database', label: 'Database', icon: Database },
    { id: 'api', label: 'API Reference', icon: Terminal },
    { id: 'routing', label: 'Routing', icon: Network },
    { id: 'frontend', label: 'Frontend', icon: Layout },
    { id: 'deployment', label: 'Deployment', icon: Rocket },
    { id: 'getting-started', label: 'Getting Started', icon: Zap },
  ]

  return (
    <div className="h-full flex overflow-hidden bg-warm-100">
      {/* Sidebar Navigation */}
      <div className="w-56 bg-white border-r border-warm-200 flex flex-col">
        <div className="p-4 border-b border-warm-200">
          <h2 className="font-semibold text-text flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-market" />
            Documentation
          </h2>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = activeSection === item.id
            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id as Section)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-market/10 text-market font-medium'
                    : 'text-text-secondary hover:bg-warm-200/50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {activeSection === 'overview' && <OverviewSection />}
          {activeSection === 'user-guide' && <UserGuideSection />}
          {activeSection === 'design-system' && <DesignSystemSection />}
          {activeSection === 'architecture' && <ArchitectureSection />}
          {activeSection === 'tech-stack' && <TechStackSection />}
          {activeSection === 'structure' && <StructureSection expandedSections={expandedSections} toggleExpanded={toggleExpanded} />}
          {activeSection === 'database' && <DatabaseSection />}
          {activeSection === 'api' && <ApiSection />}
          {activeSection === 'routing' && <RoutingSection />}
          {activeSection === 'frontend' && <FrontendSection />}
          {activeSection === 'deployment' && <DeploymentSection />}
          {activeSection === 'getting-started' && <GettingStartedSection />}
        </div>
      </div>
    </div>
  )
}

// Section Components

function OverviewSection() {
  const features = [
    { icon: LayoutGrid, title: 'Product Manager IDE', description: 'Unified workspace with dashboard, canvas, smart docs, and entity management modes for product discovery.', color: 'bg-market/10 text-market' },
    { icon: Globe, title: 'Ontology Browser', description: 'Visual exploration of RDF/OWL ontologies with interactive graph visualization using XYFlow.', color: 'bg-business/10 text-business' },
    { icon: BookOpen, title: 'Knowledge Base', description: 'RAG-powered knowledge management with training guides and AI-assisted Q&A across your sources.', color: 'bg-solution/10 text-solution' },
    { icon: FileText, title: 'Smart Documents', description: 'AI-powered document processing with multi-pass extraction (entities, facets, and visual analysis).', color: 'bg-market/10 text-market' },
    { icon: Compass, title: 'Journey Framework', description: 'Configurable PMF journey with phases, activities, and artifact tracking for structured discovery.', color: 'bg-business/10 text-business' },
    { icon: Bot, title: 'AI Coaching', description: 'Claude AI integration for intelligent coaching, validation, and suggestions throughout the platform.', color: 'bg-solution/10 text-solution' },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">Intellispot Platform</h1>
        <p className="text-text-secondary">
          Product Manager IDE for structured product discovery, combining ontology-driven knowledge graphs with AI-powered document analysis.
          Features multi-tenant workspaces, configurable journey frameworks, business model canvases, and RAG-powered knowledge bases.
        </p>
      </div>

      {/* Three Layers Banner */}
      <div className="bg-white rounded-xl p-5 border border-warm-200">
        <h3 className="font-semibold text-market mb-3 flex items-center gap-2">
          <Layers className="w-5 h-5" />
          Market-Business-Solution Ontology
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          The MBS triad structures all knowledge extraction and organization around three key questions:
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-market/5 rounded-lg p-3 border border-market/10">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-market"></span>
              <strong className="text-market">Market Layer</strong>
            </div>
            <p className="text-xs text-text-secondary mb-2">WHO are you building for?</p>
            <div className="flex flex-wrap gap-1">
              {['Persona', 'Segment', 'Pain', 'Gain', 'Job', 'Competitor'].map(e => (
                <span key={e} className="bg-market/10 text-market text-xs px-1.5 py-0.5 rounded">{e}</span>
              ))}
            </div>
          </div>
          <div className="bg-business/5 rounded-lg p-3 border border-business/10">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-business"></span>
              <strong className="text-business">Business Layer</strong>
            </div>
            <p className="text-xs text-text-secondary mb-2">HOW will you create value?</p>
            <div className="flex flex-wrap gap-1">
              {['Value Prop', 'Channel', 'Revenue', 'Strategy', 'Risk', 'Decision'].map(e => (
                <span key={e} className="bg-business/10 text-business text-xs px-1.5 py-0.5 rounded">{e}</span>
              ))}
            </div>
          </div>
          <div className="bg-solution/5 rounded-lg p-3 border border-solution/10">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-solution"></span>
              <strong className="text-solution">Solution Layer</strong>
            </div>
            <p className="text-xs text-text-secondary mb-2">WHAT will you build?</p>
            <div className="flex flex-wrap gap-1">
              {['Feature', 'Requirement', 'Epic', 'Milestone', 'Sprint', 'Component'].map(e => (
                <span key={e} className="bg-solution/10 text-solution text-xs px-1.5 py-0.5 rounded">{e}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map((feature, idx) => {
          const Icon = feature.icon
          return (
            <div key={idx} className="bg-white rounded-xl p-5 border border-warm-200 hover:shadow-md transition-shadow">
              <div className={`w-10 h-10 rounded-lg ${feature.color} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5" />
              </div>
              <h4 className="font-semibold text-text mb-1">{feature.title}</h4>
              <p className="text-sm text-text-secondary">{feature.description}</p>
            </div>
          )
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { value: '44', label: 'Classes' },
          { value: '30+', label: 'API Routes' },
          { value: '60+', label: 'Components' },
          { value: '6', label: 'Workspace Modes' },
        ].map((stat, idx) => (
          <div key={idx} className="bg-white rounded-xl p-4 border border-warm-200 text-center">
            <div className="text-2xl font-bold text-market">{stat.value}</div>
            <div className="text-xs text-text-secondary uppercase tracking-wide">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function UserGuideSection() {
  const features = [
    {
      id: 'workspace',
      icon: LayoutGrid,
      title: 'Workspace',
      color: 'market',
      description: 'Unified Product Manager IDE with multiple modes for structured product discovery.',
      steps: [
        'Dashboard mode: View project stats, health scores, and recent activity',
        'Canvas mode: Work with business model canvases and frameworks',
        'Smart Doc mode: Create and edit AI-assisted documents',
        'Entities mode: Manage ontology entities with inline editing',
        'Flow mode: Visual workflow builder for process design',
        'Switch modes using tabs in the workspace header'
      ],
      tips: [
        'Use the journey panel to track your progress through PMF phases',
        'Dashboard shows entity counts and phase completion status',
        'Each mode is optimized for specific types of work'
      ]
    },
    {
      id: 'canvas',
      icon: LayoutGrid,
      title: 'Canvas Explorer',
      color: 'business',
      description: 'Create and manage business model canvases with AI coaching support.',
      steps: [
        'Select from templates: Business Model Canvas, Lean Canvas, Value Proposition Canvas',
        'Create canvas instances from industry-specific templates',
        'Populate canvas sections with entities from your ontology',
        'Use AI Coach for guided section completion and validation',
        'View section coverage and get suggestions for improvement',
        'Export canvases as PDF for presentations'
      ],
      tips: [
        'Industry templates provide pre-filled starting points',
        'AI Coach can validate your canvas against best practices',
        'Link canvas sections to ontology for full traceability'
      ]
    },
    {
      id: 'knowledge',
      icon: BookOpen,
      title: 'Knowledge Base',
      color: 'solution',
      description: 'RAG-powered knowledge management with AI-assisted querying.',
      steps: [
        'Upload sources (PDFs, documents, interview transcripts)',
        'Sources are chunked and embedded for semantic search',
        'Use "Ask Knowledge Base" to query across all sources',
        'View source citations with extracted quotes',
        'Training Guide: Learn from structured guides with AI assistance',
        'Chat with AI to explore your knowledge base interactively'
      ],
      tips: [
        'Upload diverse sources for comprehensive coverage',
        'Training guides provide structured learning paths',
        'AI answers include citations to source documents'
      ]
    },
    {
      id: 'skerpa-guide',
      icon: Bot,
      title: 'Skerpa AI Guide',
      color: 'business',
      description: 'Skerpa is your always-on AI copilot. It knows your journey, your project data, and what you\'re looking at — and proactively suggests what to do next.',
      steps: [
        'Open the Skerpa panel — guide cards appear based on your journey progress and project state',
        'Cards are sorted by urgency: critical gaps first, then phase activities, then general advice',
        'Click any card to start a focused conversation with full project context',
        'Switch views — Skerpa detects the page and offers contextual help (e.g., "Help me map my competitors")',
        'Skerpa always knows: your goal, current phase, entity counts, domain scores, and what\'s on screen',
        'Ask anything — Skerpa references your actual personas, competitors, pains, and features by name',
      ],
      tips: [
        'Select a journey goal in the Workspace to unlock personalized phase guidance',
        'Skerpa adapts when you switch views — it reads the page data and suggests relevant actions',
        'Clear the chat to see fresh guide cards based on your latest project state',
        'Upload documents and transcripts — Skerpa uses extracted insights to give better advice',
        'View the full architecture: Help → docs/diagrams/proactive-guide-architecture.html',
      ]
    },
    {
      id: 'interviews',
      icon: MessageSquare,
      title: 'Interviews',
      color: 'market',
      description: 'Upload and analyze customer interview transcripts to extract insights.',
      steps: [
        'Create a new interview source or select existing one',
        'Upload interview transcripts (PDF or text)',
        'AI automatically extracts key themes and quotes',
        'Review extracted facets: needs, pain points, goals',
        'Tag and categorize insights across interviews',
        'Link findings to personas in your ontology'
      ],
      tips: [
        'Upload multiple interviews to identify patterns',
        'Use consistent interview scripts for better analysis',
        'Cross-reference insights with discovery findings'
      ]
    },
    {
      id: 'smartdocs',
      icon: FileEdit,
      title: 'Smart Docs',
      color: 'business',
      description: 'AI-powered document processing with multi-pass entity and facet extraction.',
      steps: [
        'Upload documents (PDF, text files)',
        'Pass 1: AI extracts entities (companies, personas, problems)',
        'Pass 2: AI extracts facets (73 types across 10 categories)',
        'Pass 3: Visual analysis extracts from images/diagrams in PDFs',
        'Review extracted facets in the Review Board',
        'Accept/reject facets and map to ontology entities'
      ],
      tips: [
        'Multi-pass yields 40-60 facets vs 6 from single-pass',
        'Visual analysis detects logos, canvases, diagrams, charts',
        'Duplicate detection prevents text/visual overlap'
      ]
    },
    {
      id: 'ontology',
      icon: Network,
      title: 'Ontology Explorer',
      color: 'solution',
      description: 'Browse and manage your project\'s semantic knowledge graph.',
      steps: [
        'View classes organized by domain (Market, Business, Solution)',
        'Expand classes to see instances and properties',
        'Create new instances using the + button',
        'Edit instance properties inline',
        'Navigate relationships between entities',
        'Search for specific classes or instances'
      ],
      tips: [
        'Use color coding: green=Market, blue=Business, amber=Solution',
        'Start with Market layer (personas, segments) before Business',
        'Keep descriptions clear and consistent'
      ]
    },
    {
      id: 'graph',
      icon: GitBranch,
      title: 'Graph View',
      color: 'market',
      description: 'Interactive visual representation of your ontology as a network graph.',
      steps: [
        'View entities as nodes and relationships as edges',
        'Drag nodes to rearrange the layout',
        'Click nodes to see details and connections',
        'Use zoom and pan to navigate large graphs',
        'Filter by domain or class type',
        'Export graph as image for presentations'
      ],
      tips: [
        'Use automatic layout for cleaner visualization',
        'Focus on specific domains to reduce complexity',
        'Graph view is great for presentations'
      ]
    },
    {
      id: 'tree',
      icon: FolderTree,
      title: 'Tree View',
      color: 'business',
      description: 'Hierarchical view of your ontology organized as a collapsible tree.',
      steps: [
        'Expand/collapse nodes to explore hierarchy',
        'See class inheritance structure',
        'View instances nested under their classes',
        'Navigate quickly through deep hierarchies',
        'Search and filter within the tree'
      ],
      tips: [
        'Tree view is ideal for understanding class hierarchy',
        'Use for quick navigation to specific entities',
        'Combine with Ontology Explorer for detailed editing'
      ]
    },
    {
      id: 'discovery',
      icon: Compass,
      title: 'Discovery Workflow',
      color: 'solution',
      description: 'Guided process to define your market, business model, and solution strategy.',
      steps: [
        'Start by selecting your project from the sidebar',
        'Answer guided questions about your target market (WHO)',
        'Define your business model and value proposition (HOW)',
        'Outline your solution architecture and features (WHAT)',
        'Use the AI assistant to refine and expand your ideas',
        'Export findings to ontology for further analysis'
      ],
      tips: [
        'Take time to thoroughly answer each question',
        'Use AI suggestions to identify blind spots',
        'Review and iterate on your discovery findings'
      ]
    },
    {
      id: 'ai-coach',
      icon: Bot,
      title: 'AI Coach',
      color: 'market',
      description: 'AI-powered coaching for product discovery and validation.',
      steps: [
        'Access AI Coach from workspace or sidebar',
        'Choose coaching mode: guided or organic conversation',
        'Ask questions about your business model and strategy',
        'Get suggestions for improving your canvases',
        'Validate assumptions with AI-powered analysis',
        'Export coaching insights to your knowledge base'
      ],
      tips: [
        'AI Coach has context about your project and ontology',
        'Use guided mode for structured coaching sessions',
        'Organic mode allows free-form exploration'
      ]
    },
    {
      id: 'settings',
      icon: Settings,
      title: 'Settings',
      color: 'business',
      description: 'Configure application settings, AI providers, and database connections.',
      steps: [
        'Configure Fuseki connection URL and credentials',
        'Set up Claude AI API key for AI features',
        'Configure Ollama URL for local LLM support',
        'Manage auto-connect preferences',
        'View connection status and health',
        'Configure industry templates and artifact types'
      ],
      tips: [
        'Test connections after configuration changes',
        'Keep API keys secure - never share them',
        'Use Ollama for local/offline AI capabilities'
      ]
    }
  ]

  const colorMap: Record<string, { bg: string, text: string, border: string, badge: string }> = {
    market: { bg: 'bg-market/10', text: 'text-market', border: 'border-market/20', badge: 'bg-market' },
    business: { bg: 'bg-business/10', text: 'text-business', border: 'border-business/20', badge: 'bg-business' },
    solution: { bg: 'bg-solution/10', text: 'text-solution', border: 'border-solution/20', badge: 'bg-solution' },
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">User Guide</h1>
        <p className="text-text-secondary">
          Learn how to use each feature of the iSpot platform to build and manage your business ontology.
        </p>
      </div>

      {/* Quick Start */}
      <div className="bg-white rounded-xl p-5 border border-warm-200">
        <h3 className="font-semibold text-market mb-3 flex items-center gap-2">
          <PlayCircle className="w-5 h-5" />
          Quick Start Workflow
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="flex items-center gap-1.5 bg-market/10 text-market px-3 py-1.5 rounded-lg">
            <span className="w-5 h-5 rounded-full bg-market text-white text-xs flex items-center justify-center">1</span>
            Discovery
          </span>
          <ArrowRight className="w-4 h-4 text-text-muted" />
          <span className="flex items-center gap-1.5 bg-business/10 text-business px-3 py-1.5 rounded-lg">
            <span className="w-5 h-5 rounded-full bg-business text-white text-xs flex items-center justify-center">2</span>
            Interviews
          </span>
          <ArrowRight className="w-4 h-4 text-text-muted" />
          <span className="flex items-center gap-1.5 bg-solution/10 text-solution px-3 py-1.5 rounded-lg">
            <span className="w-5 h-5 rounded-full bg-solution text-white text-xs flex items-center justify-center">3</span>
            Ontology
          </span>
          <ArrowRight className="w-4 h-4 text-text-muted" />
          <span className="flex items-center gap-1.5 bg-market/10 text-market px-3 py-1.5 rounded-lg">
            <span className="w-5 h-5 rounded-full bg-market text-white text-xs flex items-center justify-center">4</span>
            Canvas
          </span>
        </div>
        <p className="text-xs text-text-muted mt-3">
          Recommended workflow: Start with Discovery to define your strategy, analyze Interviews for insights, build your Ontology, then visualize on Canvas.
        </p>
      </div>

      {/* Feature Cards */}
      <div className="space-y-4">
        {features.map((feature) => {
          const Icon = feature.icon
          const colors = colorMap[feature.color]
          return (
            <div key={feature.id} className="bg-white rounded-xl border border-warm-200 overflow-hidden">
              {/* Header */}
              <div className={`${colors.bg} px-5 py-4 border-b ${colors.border}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${colors.badge} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${colors.text}`}>{feature.title}</h3>
                    <p className="text-sm text-text-secondary">{feature.description}</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 grid md:grid-cols-2 gap-6">
                {/* Steps */}
                <div>
                  <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    How to Use
                  </h4>
                  <ol className="space-y-2">
                    {feature.steps.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                        <span className={`w-5 h-5 rounded-full ${colors.bg} ${colors.text} text-xs flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          {idx + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Tips */}
                <div>
                  <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Tips
                  </h4>
                  <ul className="space-y-2">
                    {feature.tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                        <span className={`w-1.5 h-1.5 rounded-full ${colors.badge} mt-2 flex-shrink-0`}></span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Entity Loading Comparison */}
      <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
        <div className="px-5 py-3 bg-warm-100 border-b border-warm-200">
          <h3 className="font-semibold text-market text-sm uppercase tracking-wide">Entity Loading by Workspace Mode</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-warm-50">
                <th className="text-left px-4 py-3 font-semibold text-text-secondary">Mode</th>
                <th className="text-left px-4 py-3 font-semibold text-text-secondary">Data Source</th>
                <th className="text-left px-4 py-3 font-semibold text-text-secondary">Hook</th>
                <th className="text-left px-4 py-3 font-semibold text-text-secondary">Entity Filtering</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-200">
              <tr>
                <td className="px-4 py-3">
                  <span className="font-medium text-business">Canvas</span>
                </td>
                <td className="px-4 py-3 text-text-secondary">
                  Artifact API (PostgreSQL)
                </td>
                <td className="px-4 py-3">
                  <code className="bg-warm-100 px-1.5 py-0.5 rounded text-xs">useCanvasArtifacts</code>
                </td>
                <td className="px-4 py-3 text-text-secondary">
                  Per-canvas instance sections with linked entities
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3">
                  <span className="font-medium text-market">Entities</span>
                </td>
                <td className="px-4 py-3 text-text-secondary">
                  Entity Tree API → SPARQL/Fuseki
                </td>
                <td className="px-4 py-3">
                  <code className="bg-warm-100 px-1.5 py-0.5 rounded text-xs">useEntityTree</code>
                </td>
                <td className="px-4 py-3 text-text-secondary">
                  Structured mode: phase-relevant classes only<br />
                  Free mode: all entities by domain
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3">
                  <span className="font-medium text-solution">Flow</span>
                </td>
                <td className="px-4 py-3 text-text-secondary">
                  Journey Store + Entity Tree API
                </td>
                <td className="px-4 py-3">
                  <code className="bg-warm-100 px-1.5 py-0.5 rounded text-xs">useFlowWorkflow</code>
                </td>
                <td className="px-4 py-3 text-text-secondary">
                  Phase-driven entity cards with AI suggestions
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="px-5 py-4 bg-warm-50 border-t border-warm-200">
          <div className="space-y-2 text-sm text-text-secondary">
            <p><strong className="text-market">Canvas:</strong> Entities linked to canvas sections are stored in PostgreSQL artifact instances. Auto-populate syncs ontology entities.</p>
            <p><strong className="text-business">Entities:</strong> Tree view queries SPARQL graph directly. Supports validation status (pending/confirmed/rejected) and inline creation.</p>
            <p><strong className="text-solution">Flow:</strong> Combines journey phases with entity tree. Shows required vs optional classes per phase with AI suggestions.</p>
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
        <div className="px-5 py-3 bg-warm-100 border-b border-warm-200">
          <h3 className="font-semibold text-market text-sm uppercase tracking-wide">Keyboard Shortcuts</h3>
        </div>
        <div className="p-5">
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { keys: ['Ctrl', 'K'], action: 'Quick search' },
              { keys: ['Ctrl', 'S'], action: 'Save current work' },
              { keys: ['Ctrl', 'Enter'], action: 'Execute query' },
              { keys: ['Esc'], action: 'Close dialogs' },
              { keys: ['Tab'], action: 'Navigate form fields' },
              { keys: ['+', '-'], action: 'Zoom in/out (Graph view)' },
            ].map((shortcut, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{shortcut.action}</span>
                <div className="flex gap-1">
                  {shortcut.keys.map((key, kidx) => (
                    <kbd key={kidx} className="px-2 py-1 bg-warm-100 border border-warm-300 rounded text-xs font-mono text-text">
                      {key}
                    </kbd>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ArchitectureSection() {
  const patterns = [
    { pattern: 'Multi-Tenancy', implementation: 'Account/Project scoping with named graphs', purpose: 'Data isolation between tenants' },
    { pattern: 'RBAC', implementation: 'Granular permissions (resource:action:scope)', purpose: 'Fine-grained access control' },
    { pattern: 'Provider Abstraction', implementation: 'Pluggable database, graph, storage, queue', purpose: 'Flexible infrastructure' },
    { pattern: 'Graph Isolation', implementation: 'SPARQL query wrapping with GRAPH clauses', purpose: 'Tenant data separation' },
    { pattern: 'Middleware Pipeline', implementation: 'Hono middleware for auth/permissions', purpose: 'Composable request handling' },
    { pattern: 'Multi-Pass Analysis', implementation: 'Entity → Facet → Visual extraction pipeline', purpose: 'High-yield document processing' },
    { pattern: 'MCP Protocol Bridge', implementation: 'Thin adapter translating MCP tools → HTTP API calls', purpose: 'AI assistant integration (Claude, WhatsApp)' },
    { pattern: 'OAuth 2.0 + PKCE', implementation: 'Built-in authorization server with dynamic client registration', purpose: 'Secure remote MCP access' },
    { pattern: 'External Connectors', implementation: 'Adapter pattern with sync orchestration (Jira)', purpose: 'Automated external data ingestion' },
    { pattern: 'Entity Deduplication', implementation: 'Exact + Jaccard similarity matching with merge/dismiss', purpose: 'Knowledge graph quality' },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">System Architecture</h1>
        <p className="text-text-secondary">
          iSpot follows a modern three-tier architecture with clear separation between presentation, business logic, and data layers.
          The system employs a dual-database strategy, AI-powered document analysis, MCP protocol bridge for AI assistants, and external connectors for data ingestion.
        </p>
      </div>

      {/* Architecture Diagram */}
      <div className="bg-white rounded-xl p-6 border border-warm-200">
        <div className="space-y-4">
          {/* Client Layer */}
          <div className="bg-market/10 rounded-lg p-4 border border-market/20">
            <h4 className="font-semibold text-market text-sm mb-3">CLIENT LAYER</h4>
            <div className="flex flex-wrap gap-2">
              {['React 19', 'Zustand', 'React Query', 'XYFlow', 'Radix UI', 'TailwindCSS'].map(tech => (
                <span key={tech} className="bg-white px-3 py-1.5 rounded-md text-sm text-text border border-warm-200">{tech}</span>
              ))}
            </div>
          </div>

          <div className="flex justify-center">
            <div className="w-0.5 h-6 bg-warm-300"></div>
          </div>

          {/* API Layer */}
          <div className="bg-business/10 rounded-lg p-4 border border-business/20">
            <h4 className="font-semibold text-business text-sm mb-3">API LAYER (Hono.js + TypeScript)</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {['Auth Routes', 'Account Routes', 'Project Routes', 'SPARQL Routes', 'OAuth Routes', 'MCP Endpoint', 'Connector Routes', 'Dedup Routes', 'CRM Routes', 'Admin Routes'].map(route => (
                <div key={route} className="bg-white px-3 py-2 rounded-md text-xs text-center border border-warm-200">
                  <div className="font-medium text-text">{route}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center">
            <div className="w-0.5 h-6 bg-warm-300"></div>
          </div>

          {/* Middleware */}
          <div className="bg-solution/10 rounded-lg p-3 border border-solution/20">
            <p className="text-sm text-solution text-center font-medium">
              MIDDLEWARE: Auth | OAuth Token | API Token | Account Context | Project Context | RBAC Permissions
            </p>
          </div>

          <div className="flex justify-center gap-32">
            <div className="w-0.5 h-6 bg-warm-300"></div>
            <div className="w-0.5 h-6 bg-warm-300"></div>
          </div>

          {/* Providers */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-market/10 rounded-lg p-4 border border-market/20">
              <h4 className="font-semibold text-market text-sm mb-1">DATABASE PROVIDER</h4>
              <p className="text-sm text-text">Drizzle ORM + PostgreSQL</p>
              <p className="text-xs text-text-secondary">Users, Sessions, Accounts, Projects, CRM</p>
            </div>
            <div className="bg-solution/10 rounded-lg p-4 border border-solution/20">
              <h4 className="font-semibold text-solution text-sm mb-1">GRAPH PROVIDER</h4>
              <p className="text-sm text-text">Apache Jena Fuseki (SPARQL)</p>
              <p className="text-xs text-text-secondary">RDF Ontology, Named Graphs, Triples</p>
            </div>
          </div>

          <div className="flex justify-center gap-32">
            <div className="w-0.5 h-6 bg-warm-300"></div>
            <div className="w-0.5 h-6 bg-warm-300"></div>
          </div>

          {/* Databases */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-market rounded-lg p-3 text-center">
              <span className="font-semibold text-white text-sm">PostgreSQL 16</span>
            </div>
            <div className="bg-solution rounded-lg p-3 text-center">
              <span className="font-semibold text-white text-sm">Apache Jena Fuseki</span>
            </div>
          </div>
        </div>
      </div>

      {/* Document Processing Pipeline */}
      <div className="bg-white rounded-xl p-6 border border-warm-200">
        <h3 className="font-semibold text-market mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Multi-Pass Document Analysis Pipeline
        </h3>
        <div className="space-y-3">
          {/* Pass 1 */}
          <div className="bg-market/10 rounded-lg p-4 border border-market/20">
            <div className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-market text-white text-sm flex items-center justify-center font-bold">1</span>
              <div>
                <h4 className="font-semibold text-market">Entity Extraction</h4>
                <p className="text-sm text-text-secondary">Companies, Personas, Problems, Features, Competitors, Trends</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="w-4 h-4 text-text-muted rotate-90" />
          </div>

          {/* Pass 2 */}
          <div className="bg-business/10 rounded-lg p-4 border border-business/20">
            <div className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-business text-white text-sm flex items-center justify-center font-bold">2</span>
              <div>
                <h4 className="font-semibold text-business">Facet Extraction (with Entity Context)</h4>
                <p className="text-sm text-text-secondary">73 facet types: customer_pain, feature_request, risk, opportunity, etc.</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="w-4 h-4 text-text-muted rotate-90" />
          </div>

          {/* Pass 3 */}
          <div className="bg-solution/10 rounded-lg p-4 border border-solution/20">
            <div className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-solution text-white text-sm flex items-center justify-center font-bold">3</span>
              <div>
                <h4 className="font-semibold text-solution">Visual Analysis (PDFs only)</h4>
                <p className="text-sm text-text-secondary">Logos, Business Canvases, Diagrams, Charts, Whiteboards, UI Screenshots</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="w-4 h-4 text-text-muted rotate-90" />
          </div>

          {/* Output */}
          <div className="bg-warm-100 rounded-lg p-4 border border-warm-300">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-market" />
              <div>
                <h4 className="font-semibold text-text">Unified Storage</h4>
                <p className="text-sm text-text-secondary">Single facets table with source_type discriminator (text | visual). Deduplication via trigram similarity.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="bg-warm-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-market">73</div>
            <div className="text-xs text-text-secondary">Facet Types</div>
          </div>
          <div className="bg-warm-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-business">10</div>
            <div className="text-xs text-text-secondary">Categories</div>
          </div>
          <div className="bg-warm-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-solution">12</div>
            <div className="text-xs text-text-secondary">Image Types</div>
          </div>
        </div>
      </div>

      {/* MCP Server & External Integrations */}
      <div className="bg-white rounded-xl p-6 border border-warm-200">
        <h3 className="font-semibold text-business mb-4 flex items-center gap-2">
          <Bot className="w-5 h-5" />
          MCP Server & External Integrations
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-business/10 rounded-lg p-4 border border-business/20">
            <h4 className="font-semibold text-business mb-2">MCP Protocol Bridge</h4>
            <p className="text-sm text-text-secondary mb-3">
              15 MCP tools expose product intelligence to AI assistants (Claude Desktop, WhatsApp bots). Zero business logic — every tool call translates to an API HTTP request.
            </p>
            <div className="flex flex-wrap gap-1">
              {['list_projects', 'get_entity_tree', 'create_entity', 'show_canvas', 'sparql_query'].map(t => (
                <span key={t} className="bg-white text-business text-xs px-2 py-0.5 rounded border border-business/20">{t}</span>
              ))}
            </div>
          </div>
          <div className="bg-market/10 rounded-lg p-4 border border-market/20">
            <h4 className="font-semibold text-market mb-2">External Connectors</h4>
            <p className="text-sm text-text-secondary mb-3">
              Sync external tools (Jira) into the analysis pipeline. Adapter pattern with sync orchestration — items are auto-converted to sources for entity extraction.
            </p>
            <div className="flex flex-wrap gap-1">
              {['Jira Cloud', 'OAuth 2.0 (3LO)', 'Incremental Sync', 'Batch Processing'].map(t => (
                <span key={t} className="bg-white text-market text-xs px-2 py-0.5 rounded border border-market/20">{t}</span>
              ))}
            </div>
          </div>
          <div className="bg-solution/10 rounded-lg p-4 border border-solution/20">
            <h4 className="font-semibold text-solution mb-2">OAuth 2.0 Authorization Server</h4>
            <p className="text-sm text-text-secondary mb-3">
              Built-in OAuth server for remote MCP access. Supports PKCE (S256), dynamic client registration (RFC 7591), and token refresh/revocation.
            </p>
            <div className="flex flex-wrap gap-1">
              {['/oauth/authorize', '/oauth/token', '/oauth/register', '/.well-known/*'].map(t => (
                <span key={t} className="bg-white text-solution text-xs px-2 py-0.5 rounded border border-solution/20">{t}</span>
              ))}
            </div>
          </div>
          <div className="bg-warm-100 rounded-lg p-4 border border-warm-300">
            <h4 className="font-semibold text-text mb-2">Entity Deduplication</h4>
            <p className="text-sm text-text-secondary mb-3">
              Automatic duplicate detection in the knowledge graph. Exact matches auto-merge; similar entities (Jaccard &gt; 0.5) are queued for review.
            </p>
            <div className="flex flex-wrap gap-1">
              {['Exact Match', 'Jaccard Similarity', 'Auto-merge (>0.8)', 'Review Queue'].map(t => (
                <span key={t} className="bg-white text-text-secondary text-xs px-2 py-0.5 rounded border border-warm-200">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Architectural Patterns */}
      <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
        <div className="px-5 py-3 bg-warm-100 border-b border-warm-200">
          <h3 className="font-semibold text-market text-sm uppercase tracking-wide">Architectural Patterns</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-warm-50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Pattern</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Implementation</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Purpose</th>
            </tr>
          </thead>
          <tbody>
            {patterns.map((row, idx) => (
              <tr key={idx} className="border-t border-warm-200 hover:bg-warm-50">
                <td className="px-5 py-3 font-medium text-text text-sm">{row.pattern}</td>
                <td className="px-5 py-3 text-text-secondary text-sm">{row.implementation}</td>
                <td className="px-5 py-3 text-text-secondary text-sm">{row.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TechStackSection() {
  const stacks = [
    {
      title: 'Backend',
      subtitle: 'Node.js API with TypeScript',
      color: 'market',
      badge: '10+ packages',
      items: ['Node.js 22', 'TypeScript 5.6', 'Hono.js 4.6', 'Drizzle ORM 0.36', 'PostgreSQL 16', 'Zod 3.23', 'Claude SDK 0.71', 'MCP SDK', 'OAuth 2.0 (PKCE)', 'Pino Logger', 'nanoid'],
      highlighted: ['Node.js 22', 'MCP SDK']
    },
    {
      title: 'Frontend',
      subtitle: 'React application with modern tooling',
      color: 'business',
      badge: '30+ packages',
      items: ['React 19.2', 'Vite 7', 'TailwindCSS 4', 'Zustand 5.0', 'TanStack Query 5.90', 'XYFlow 12.10', 'Radix UI', 'TipTap Editor', 'RDFLib 2.3', 'Lucide Icons 0.555'],
      highlighted: ['React 19.2', 'TanStack Query 5.90']
    },
    {
      title: 'Infrastructure',
      subtitle: 'Containerized deployment stack',
      color: 'solution',
      badge: '5 services',
      items: ['Docker', 'Docker Compose', 'PostgreSQL 16-alpine', 'Apache Jena Fuseki', 'Ollama (optional)', 'MCP Server (stdio/HTTP)', 'drizzle-kit'],
      highlighted: ['Docker', 'MCP Server (stdio/HTTP)']
    },
  ]

  const colorMap: Record<string, string> = {
    market: 'bg-market text-white',
    business: 'bg-business text-white',
    solution: 'bg-solution text-white',
  }

  const highlightMap: Record<string, string> = {
    market: 'bg-market/10 text-market',
    business: 'bg-business/10 text-business',
    solution: 'bg-solution/10 text-solution',
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">Technology Stack</h1>
        <p className="text-text-secondary">
          Modern, production-ready technologies powering the iSpot platform.
        </p>
      </div>

      <div className="space-y-4">
        {stacks.map((stack, idx) => (
          <div key={idx} className="bg-white rounded-xl border border-warm-200 overflow-hidden">
            <div className={`${colorMap[stack.color]} px-5 py-4 flex justify-between items-center`}>
              <div>
                <h3 className="font-semibold">{stack.title}</h3>
                <p className="text-sm opacity-90">{stack.subtitle}</p>
              </div>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">{stack.badge}</span>
            </div>
            <div className="p-5">
              <div className="flex flex-wrap gap-2">
                {stack.items.map(item => (
                  <span
                    key={item}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${
                      stack.highlighted.includes(item)
                        ? highlightMap[stack.color] + ' border-transparent'
                        : 'bg-white border-warm-200 text-text'
                    }`}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StructureSection({ expandedSections, toggleExpanded }: { expandedSections: Set<string>, toggleExpanded: (s: string) => void }) {
  const structure = [
    {
      name: 'apps/',
      comment: 'Application packages',
      children: [
        {
          name: 'api/',
          comment: 'Node.js/TypeScript Backend',
          children: [
            { name: 'src/', children: [
              { name: 'index.ts', comment: 'Entry point - Hono app' },
              { name: 'routes/', comment: '30+ route files', children: [
                { name: 'artifacts.ts', comment: 'Canvas & document artifacts' },
                { name: 'canvasAI.ts', comment: 'AI-powered canvas operations' },
                { name: 'journey.ts', comment: 'PMF journey framework' },
                { name: 'knowledge.ts', comment: 'RAG knowledge base' },
                { name: 'sparql.ts', comment: 'SPARQL query/update' },
              ]},
              { name: 'services/', comment: 'Business logic layer', children: [
                { name: 'ai/', comment: 'Claude & Ollama integration' },
                { name: 'artifact/', comment: 'Artifact management' },
                { name: 'document/', comment: 'Document processing' },
              ]},
              { name: 'database/', children: [
                { name: 'schema/', comment: 'Drizzle schema files' },
                { name: 'seed/', comment: 'Seed data scripts' },
              ]},
              { name: 'middleware/', comment: 'Auth, OAuth, API token, permissions' },
            ]},
            { name: 'drizzle/', comment: 'Database migrations' },
          ]
        },
        {
          name: 'mcp-server/',
          comment: 'MCP Protocol Bridge for AI Assistants',
          children: [
            { name: 'src/', children: [
              { name: 'index.ts', comment: 'MCP SDK setup, stdio transport' },
              { name: 'client.ts', comment: 'HTTP client to API' },
              { name: 'tools/', comment: '15 MCP tools (projects, entities, canvas, graph, health)' },
              { name: 'resources/', comment: 'Ontology + Canvas UI resources' },
              { name: 'ui/', comment: 'Interactive canvas MCP App' },
            ]},
          ]
        },
        {
          name: 'web/',
          comment: 'React Frontend',
          children: [
            { name: 'src/', children: [
              { name: 'App.tsx', comment: 'Root component & routing' },
              { name: 'components/', comment: '60+ UI components', children: [
                { name: 'workspace/', comment: 'Product Manager IDE' },
                { name: 'canvas/', comment: 'Business canvases' },
                { name: 'knowledge/', comment: 'Knowledge base & guides' },
                { name: 'discovery/', comment: 'Discovery workflow' },
              ]},
              { name: 'stores/', comment: '15 Zustand stores' },
              { name: 'hooks/', comment: 'Custom React hooks' },
              { name: 'lib/', comment: 'API client & utilities' },
            ]},
          ]
        },
      ]
    },
    {
      name: 'packages/',
      comment: 'Shared packages',
      children: [
        { name: 'shared/', comment: 'Shared types & validation' },
      ]
    },
    { name: 'docker-compose.yml', comment: 'Multi-service orchestration' },
    { name: 'docs/', comment: 'Documentation & plans' },
  ]

  const renderTree = (items: any[], depth = 0) => (
    <div className={depth > 0 ? 'ml-4 border-l border-warm-300 pl-3' : ''}>
      {items.map((item, idx) => {
        const isFolder = item.children
        const isExpanded = expandedSections.has(item.name)
        return (
          <div key={idx} className="py-0.5">
            <div
              className={`flex items-center gap-2 py-1 ${isFolder ? 'cursor-pointer hover:text-market' : ''}`}
              onClick={() => isFolder && toggleExpanded(item.name)}
            >
              {isFolder && (
                isExpanded ? <ChevronDown className="w-3 h-3 text-text-secondary" /> : <ChevronRight className="w-3 h-3 text-text-secondary" />
              )}
              <span className={isFolder ? 'text-market font-medium' : 'text-solution'}>{item.name}</span>
              {item.comment && <span className="text-text-muted text-xs">{'// ' + item.comment}</span>}
            </div>
            {isFolder && isExpanded && renderTree(item.children, depth + 1)}
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">Project Structure</h1>
        <p className="text-text-secondary">
          Monorepo structure with separate API and frontend packages.
        </p>
      </div>

      <div className="bg-slate-800 rounded-xl p-5 font-mono text-sm">
        <div className="text-market font-semibold mb-2">ispot2/</div>
        {renderTree(structure)}
      </div>
    </div>
  )
}

function DatabaseSection() {
  const tables = [
    { name: 'users', purpose: 'User authentication & profiles', fields: 'id (UUID), email, passwordHash, isSuperAdmin' },
    { name: 'sessions', purpose: 'Active user sessions', fields: 'id (nanoid), userId, expiresAt' },
    { name: 'accounts', purpose: 'Multi-tenant organizations', fields: 'id, name, slug, plan, status' },
    { name: 'projects', purpose: 'Workspaces within accounts', fields: 'id, accountId, name, graphUri' },
    { name: 'artifactTemplates', purpose: 'Canvas/doc template definitions', fields: 'id, accountId, key, type, domain, structure' },
    { name: 'artifactInstances', purpose: 'Canvas/doc instances', fields: 'id, projectId, templateId, name, content' },
    { name: 'journeyFrameworks', purpose: 'PMF journey configurations', fields: 'id, accountId, name, config (JSONB)' },
    { name: 'projectJourneys', purpose: 'Project journey progress', fields: 'id, projectId, currentPhaseId, phaseProgress' },
    { name: 'knowledgeSources', purpose: 'RAG knowledge sources', fields: 'id, projectId, name, status, chunkCount' },
    { name: 'knowledgeChunks', purpose: 'Embedded document chunks', fields: 'id, sourceId, content, embedding, metadata' },
    { name: 'apiTokens', purpose: 'Bearer tokens for MCP/API access', fields: 'id, token, name, userId, accountId, expiresAt' },
    { name: 'oauthClients', purpose: 'Registered OAuth 2.0 clients', fields: 'id, clientId, clientSecret, name, redirectUris' },
    { name: 'oauthAccessTokens', purpose: 'OAuth access tokens', fields: 'id, token, clientId, userId, scope, expiresAt' },
    { name: 'oauthRefreshTokens', purpose: 'OAuth refresh tokens', fields: 'id, token, accessTokenId, expiresAt' },
    { name: 'externalConnectors', purpose: 'External integrations (Jira)', fields: 'id, projectId, type, status, config, oauthTokens' },
    { name: 'syncJobs', purpose: 'Connector sync job history', fields: 'id, connectorId, status, itemsSynced, errors' },
    { name: 'entityDedupEvents', purpose: 'Dedup merge/dismiss activity log', fields: 'id, projectId, action, entityUris, similarity' },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">Database Schema</h1>
        <p className="text-text-secondary">
          Dual-database architecture combining PostgreSQL and Apache Jena Fuseki.
        </p>
      </div>

      {/* PostgreSQL */}
      <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
        <div className="bg-market px-5 py-4 flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-white">PostgreSQL Tables</h3>
            <p className="text-sm text-white/80">Relational data for auth, tenancy, artifacts, and journeys</p>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white">30+ tables</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-warm-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Table</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Purpose</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Key Fields</th>
              </tr>
            </thead>
            <tbody>
              {tables.map((row, idx) => (
                <tr key={idx} className="border-t border-warm-200 hover:bg-warm-50">
                  <td className="px-5 py-3"><code className="bg-warm-100 px-2 py-0.5 rounded text-market text-sm">{row.name}</code></td>
                  <td className="px-5 py-3 text-text-secondary text-sm">{row.purpose}</td>
                  <td className="px-5 py-3 text-text-secondary text-sm font-mono text-xs">{row.fields}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fuseki */}
      <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
        <div className="bg-business px-5 py-4 flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-white">Graph Database (Fuseki)</h3>
            <p className="text-sm text-white/80">RDF triples organized in named graphs</p>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white">3 domains</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-solution/10 rounded-lg p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-solution mt-0.5" />
            <div>
              <p className="font-medium text-text">Named Graphs</p>
              <p className="text-sm text-text-secondary">Each project has a unique <code className="bg-warm-200 px-1.5 py-0.5 rounded text-market">graphUri</code> creating isolated named graphs in Fuseki. All SPARQL queries are wrapped with <code className="bg-warm-200 px-1.5 py-0.5 rounded text-market">GRAPH &lt;uri&gt; {'{ ... }'}</code> clauses.</p>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-text mb-3">Ontology Domains</h4>
            <div className="flex flex-wrap gap-3">
              <span className="flex items-center gap-2 bg-market/10 text-market px-4 py-2 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-market"></span>
                Market Domain <code className="ml-1 text-xs bg-white/50 px-1.5 py-0.5 rounded">market:</code>
              </span>
              <span className="flex items-center gap-2 bg-business/10 text-business px-4 py-2 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-business"></span>
                Business Domain <code className="ml-1 text-xs bg-white/50 px-1.5 py-0.5 rounded">business:</code>
              </span>
              <span className="flex items-center gap-2 bg-solution/10 text-solution px-4 py-2 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-solution"></span>
                Solution Domain <code className="ml-1 text-xs bg-white/50 px-1.5 py-0.5 rounded">solution:</code>
              </span>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-text mb-2">Sample SPARQL Query</h4>
            <pre className="bg-slate-800 text-slate-200 p-4 rounded-lg text-sm overflow-x-auto">
{`PREFIX market: <http://ispot.io/ontology/market#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

SELECT ?persona ?label ?description
WHERE {
  ?persona a market:Persona ;
           rdfs:label ?label ;
           market:description ?description .
}`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  )
}

function ApiSection() {
  const endpoints = [
    { method: 'POST', path: '/api/auth/register', description: 'Register a new user account with automatic organization creation.', body: '{ "email": "user@example.com", "password": "...", "name": "John Doe" }' },
    { method: 'POST', path: '/api/auth/login', description: 'Authenticate user and create session. Sets HttpOnly cookie (7 days).', body: '{ "email": "user@example.com", "password": "..." }' },
    { method: 'GET', path: '/api/auth/me', description: 'Get current authenticated user information.' },
    { method: 'GET', path: '/api/.../artifacts/templates', description: 'List available artifact templates (canvases, docs).', body: undefined },
    { method: 'POST', path: '/api/.../artifacts/instances', description: 'Create a new artifact instance from template.', body: '{ "templateId": "...", "name": "My Canvas" }' },
    { method: 'GET', path: '/api/.../journey', description: 'Get project journey progress and phase status.', body: undefined },
    { method: 'POST', path: '/api/.../journey/phase/:phaseId/complete', description: 'Mark a journey phase as complete.', body: undefined },
    { method: 'POST', path: '/api/.../knowledge/sources', description: 'Upload a knowledge source for RAG processing.', body: '{ "name": "Interview 1", "content": "..." }' },
    { method: 'POST', path: '/api/.../knowledge/query', description: 'Query knowledge base with semantic search.', body: '{ "query": "What are customer pain points?" }' },
    { method: 'POST', path: '/api/.../canvas-ai/coach', description: 'AI coaching for canvas section completion.', body: '{ "instanceId": "...", "sectionKey": "..." }' },
    { method: 'POST', path: '/api/auth/tokens', description: 'Create API bearer token for MCP server or external access.', body: '{ "name": "Claude Desktop" }' },
    { method: 'POST', path: '/oauth/register', description: 'Dynamic OAuth client registration (RFC 7591).', body: '{ "client_name": "...", "redirect_uris": [...] }' },
    { method: 'POST', path: '/oauth/token', description: 'Exchange auth code for access + refresh tokens (PKCE/S256).', body: '{ "grant_type": "authorization_code", "code": "...", "code_verifier": "..." }' },
    { method: 'POST', path: '/mcp', description: 'MCP Streamable HTTP endpoint (JSON-RPC). Supports GET (SSE), POST (request), DELETE (close).', body: undefined },
    { method: 'GET', path: '/api/.../connectors', description: 'List external connectors for a project (Jira, etc).', body: undefined },
    { method: 'POST', path: '/api/.../connectors/:id/sync', description: 'Trigger sync for an external connector.', body: '{ "mode": "full" | "incremental" }' },
    { method: 'GET', path: '/api/.../deduplication/candidates', description: 'List pending entity merge candidates with similarity scores.', body: undefined },
    { method: 'POST', path: '/api/.../deduplication/merge', description: 'Merge two duplicate entities in the knowledge graph.', body: '{ "canonicalUri": "...", "deprecatedUri": "..." }' },
  ]

  const methodColors: Record<string, string> = {
    GET: 'bg-market/10 text-market',
    POST: 'bg-solution/10 text-solution',
    PUT: 'bg-business/10 text-business',
    DELETE: 'bg-red-100 text-red-700',
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">API Reference</h1>
        <p className="text-text-secondary">
          RESTful API built with Hono.js. Supports session cookies, Bearer tokens (API tokens), and OAuth 2.0 access tokens. Includes MCP Streamable HTTP endpoint.
        </p>
      </div>

      <div className="space-y-4">
        {endpoints.map((endpoint, idx) => (
          <div key={idx} className="bg-white rounded-xl border border-warm-200 overflow-hidden">
            <div className="px-5 py-3 bg-warm-50 border-b border-warm-200 flex items-center gap-3">
              <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase ${methodColors[endpoint.method]}`}>
                {endpoint.method}
              </span>
              <code className="font-medium text-text text-sm">{endpoint.path}</code>
            </div>
            <div className="p-5">
              <p className="text-text-secondary text-sm mb-3">{endpoint.description}</p>
              {endpoint.body && (
                <div>
                  <h4 className="text-xs font-semibold text-text-secondary uppercase mb-2">Request Body</h4>
                  <pre className="bg-slate-800 text-slate-200 p-3 rounded-lg text-xs overflow-x-auto">{endpoint.body}</pre>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function FrontendSection() {
  const stores = [
    { name: 'authStore', purpose: 'Authentication', state: 'user, isAuthenticated, currentAccount' },
    { name: 'projectStore', purpose: 'Project context', state: 'currentProject, projects' },
    { name: 'workspaceStore', purpose: 'Workspace modes', state: 'activeMode, currentPhaseId, detailPanes' },
    { name: 'journeyStore', purpose: 'Journey progress', state: 'phases, completedActivities, entityCounts' },
    { name: 'aiStore', purpose: 'AI integration', state: 'isConfigured, provider, messages' },
    { name: 'connectionStore', purpose: 'Fuseki connection', state: 'isConnected, autoConnect' },
    { name: 'discoveryAssistantStore', purpose: 'AI coaching', state: 'sessions, messages, context' },
    { name: 'documentStore', purpose: 'Document processing', state: 'sources, facets, status' },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">Frontend Architecture</h1>
        <p className="text-text-secondary">
          React 19 application with Zustand for state management and React Query for data fetching.
        </p>
      </div>

      {/* State Management */}
      <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
        <div className="px-5 py-3 bg-warm-100 border-b border-warm-200">
          <h3 className="font-semibold text-market text-sm uppercase tracking-wide">State Management (Zustand)</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-warm-50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Store</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Purpose</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Key State</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((row, idx) => (
              <tr key={idx} className="border-t border-warm-200 hover:bg-warm-50">
                <td className="px-5 py-3"><code className="bg-warm-100 px-2 py-0.5 rounded text-market text-sm">{row.name}</code></td>
                <td className="px-5 py-3 text-text-secondary text-sm">{row.purpose}</td>
                <td className="px-5 py-3 text-text-secondary text-sm font-mono text-xs">{row.state}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Component Hierarchy */}
      <div>
        <h3 className="font-semibold text-text mb-3">Component Hierarchy</h3>
        <pre className="bg-slate-800 text-slate-200 p-5 rounded-xl text-sm overflow-x-auto">
{`App
├── AuthProvider
│   ├── LoginForm
│   └── RegisterForm
├── ProjectSelector
├── MainLayout
│   ├── Sidebar (navigation)
│   ├── WorkspaceHeader (mode tabs)
│   └── ContentArea
│       ├── WorkspacePage      // Product Manager IDE
│       │   ├── DashboardMode
│       │   ├── CanvasMode
│       │   ├── SmartDocMode
│       │   ├── EntitiesMode
│       │   └── FlowMode
│       ├── CanvasExplorer     // Business canvases
│       ├── KnowledgeBase      // RAG + Training Guide
│       ├── OntologyExplorer   // Entity management
│       └── DiscoveryWorkflow
└── QueryClientProvider        // TanStack Query`}
        </pre>
      </div>
    </div>
  )
}

function DeploymentSection() {
  const services = [
    { name: 'db (PostgreSQL)', image: 'postgres:16-alpine', port: '5433', deps: '-' },
    { name: 'fuseki', image: 'stain/jena-fuseki:latest', port: '3031', deps: '-' },
    { name: 'api (native)', image: 'npm run dev:api', port: '3002', deps: 'db, fuseki' },
    { name: 'web (native)', image: 'npm run dev:web', port: '5174', deps: 'api' },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">Deployment</h1>
        <p className="text-text-secondary">
          Docker Compose orchestration with 5 services for local development and production deployment.
        </p>
      </div>

      {/* Services Table */}
      <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
        <div className="bg-solution px-5 py-4 flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-white">Docker Compose Services</h3>
            <p className="text-sm text-white/80">Multi-service container orchestration</p>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white">4 services</span>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-warm-50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Service</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Image</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Port</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase">Dependencies</th>
            </tr>
          </thead>
          <tbody>
            {services.map((row, idx) => (
              <tr key={idx} className="border-t border-warm-200 hover:bg-warm-50">
                <td className="px-5 py-3 font-semibold text-text text-sm">{row.name}</td>
                <td className="px-5 py-3 text-text-secondary text-sm">{row.image}</td>
                <td className="px-5 py-3 text-text-secondary text-sm">{row.port}</td>
                <td className="px-5 py-3 text-text-secondary text-sm">{row.deps}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Environment Variables */}
      <div className="space-y-4">
        <h3 className="font-semibold text-text">Environment Variables</h3>

        <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
          <div className="px-4 py-2 bg-warm-100 border-b border-warm-200">
            <h4 className="font-medium text-market text-sm">Backend (apps/api/.env)</h4>
          </div>
          <pre className="p-4 text-sm overflow-x-auto bg-slate-800 text-slate-200">
{`# Server
PORT=3002
HOST=0.0.0.0
NODE_ENV=development

# Security
SESSION_SECRET=your-super-secret-key
CORS_ORIGIN=http://localhost:5174

# Database (Docker PostgreSQL on port 5433)
DATABASE_URL=postgres://ispot:ispot@localhost:5433/ispot

# Graph Database
FUSEKI_URL=http://localhost:3031
FUSEKI_DATASET=ispot

# AI Providers
ANTHROPIC_API_KEY=your-claude-api-key
OLLAMA_URL=http://localhost:11434`}
          </pre>
        </div>

        <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
          <div className="px-4 py-2 bg-warm-100 border-b border-warm-200">
            <h4 className="font-medium text-market text-sm">Frontend (apps/web/.env.local)</h4>
          </div>
          <pre className="p-4 text-sm overflow-x-auto bg-slate-800 text-slate-200">
{`VITE_API_URL=http://localhost:3002`}
          </pre>
        </div>
      </div>
    </div>
  )
}

function GettingStartedSection() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text mb-2">Getting Started</h1>
        <p className="text-text-secondary">
          Quick setup guide for development environment.
        </p>
      </div>

      {/* Prerequisites */}
      <div className="bg-white rounded-xl p-5 border border-warm-200">
        <h3 className="font-semibold text-market text-sm uppercase tracking-wide mb-3">Prerequisites</h3>
        <div className="flex flex-wrap gap-2">
          <span className="bg-warm-100 px-3 py-1.5 rounded-lg text-sm text-text border border-warm-200">Node.js 22+</span>
          <span className="bg-warm-100 px-3 py-1.5 rounded-lg text-sm text-text border border-warm-200">Docker Desktop</span>
          <span className="bg-warm-100 px-3 py-1.5 rounded-lg text-sm text-text border border-warm-200">npm (via monorepo)</span>
          <span className="bg-warm-100 px-3 py-1.5 rounded-lg text-sm text-text border border-warm-200">Ollama (optional)</span>
        </div>
      </div>

      {/* Quick Start */}
      <div>
        <h3 className="font-semibold text-text mb-3">Quick Start</h3>
        <pre className="bg-slate-800 text-slate-200 p-5 rounded-xl text-sm overflow-x-auto">
{`# 1. Start Docker services (PostgreSQL, Fuseki)
docker-compose up -d db fuseki

# 2. Run database migrations and seed
npm run db:migrate && npm run db:seed

# 3. Start the development servers
npm run dev          # Both API and Web
# OR separately:
npm run dev:api      # API only (port 3002)
npm run dev:web      # Web only (port 5174)

# Access the application
# Frontend: http://localhost:5174
# API: http://localhost:3002
# Fuseki Admin: http://localhost:3031`}
        </pre>
      </div>

      {/* Local Development */}
      <div>
        <h3 className="font-semibold text-text mb-3">Service Health Check</h3>
        <pre className="bg-slate-800 text-slate-200 p-5 rounded-xl text-sm overflow-x-auto">
{`# Check Docker containers
docker ps                              # Should show: ispot2-db, ispot2-fuseki

# Check API health
curl http://localhost:3002/health      # Should return {"status":"healthy"}

# Check Ollama (if using local AI)
curl http://localhost:11434/api/tags   # Should return model list`}
        </pre>
      </div>

      {/* Credentials */}
      <div className="bg-market/10 rounded-xl p-5 border border-market/20">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-market mt-0.5" />
          <div>
            <p className="font-semibold text-text">Default Credentials</p>
            <p className="text-sm text-text-secondary">
              PostgreSQL: <code className="bg-white px-1.5 py-0.5 rounded text-market">ispot:ispot@localhost:5433</code>
              {' | '}
              Fuseki: <code className="bg-white px-1.5 py-0.5 rounded text-market">localhost:3031</code>
            </p>
          </div>
        </div>
      </div>

      {/* Database Commands */}
      <div>
        <h3 className="font-semibold text-text mb-3">Database Commands</h3>
        <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
          <table className="w-full">
            <tbody>
              <tr className="border-b border-warm-200">
                <td className="px-5 py-3"><code className="bg-warm-100 px-2 py-0.5 rounded text-market text-sm">npm run db:migrate</code></td>
                <td className="px-5 py-3 text-text-secondary text-sm">Apply database migrations</td>
              </tr>
              <tr className="border-b border-warm-200">
                <td className="px-5 py-3"><code className="bg-warm-100 px-2 py-0.5 rounded text-market text-sm">npm run db:push</code></td>
                <td className="px-5 py-3 text-text-secondary text-sm">Push schema directly (dev only)</td>
              </tr>
              <tr className="border-b border-warm-200">
                <td className="px-5 py-3"><code className="bg-warm-100 px-2 py-0.5 rounded text-market text-sm">npm run db:seed</code></td>
                <td className="px-5 py-3 text-text-secondary text-sm">Seed database with initial data</td>
              </tr>
              <tr>
                <td className="px-5 py-3"><code className="bg-warm-100 px-2 py-0.5 rounded text-market text-sm">npm run db:studio</code></td>
                <td className="px-5 py-3 text-text-secondary text-sm">Open Drizzle Studio (visual DB browser)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
